import axios from "axios";
import forge from "node-forge";
import { cloneElement, createContext } from "react";
import { read, utils } from "xlsx";

export const ServerUrl = process.env.BACKEND_URL;

// Public Key
export const publicKey = `
-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAjU8t9z0UtiSaO7JUHvMp
ln1VFJ3QJgoKyOorhWvt2Xj0jZ9d1QpD2PjV/A5n3VoOsnLzkhHOlXooSqHbuB2D
n4g6fzbI2N+Y0KggtZGhjtwVVEyaI3LMWVI+S80gRrcY+sP/SvPniuMJoJDfc1Gf
KK5lmfB2CjTCV0LZBMWA9JSiN4ngRhVQ69sAOhAo1HaY7R+CXhlSPTKfcZqkpSjj
n6DyDaQXI5YeoQOsDxDWelqftfr8lTSDh+tOkjGL7irUStaxG5zYJyYAF1OMM4Ae
x2NLD373UGtdlOT7STi3UvsNdvDpDrY5O1HteNaxC1S815UZU3U/u5lvANQB16aO
pQIDAQAB
-----END PUBLIC KEY-----
`;
export const siteKey = "6LfhyIYpAAAAAD7cGt4Ece4-00xqR00a32FRMDI3";

/**
 * @template T
 * @param {T} object
 * @returns {T}
 */
export function copy(object) {
  return Object.assign({}, object);
}

// default forms/states
export const defaults = {
  login: { email: "", password: "" },
  register: {
    personal: {
      firstName: "",
      lastName: "",
      email: "",
      participantId: "",
    },
    account: {
      drivingLicenseId: "",
      password: "",
      confirmPassword: "",
    },
  },
  changePassword: {
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  },
  resetPassword: {
    email: "",
    newPassword: "",
    confirmNewPassword: "",
  },
  instructionsHA: {
    instructions: {
      "top-view": [],
      "driver-view": [],
    },
    "instructions-practice": {
      "top-view": [],
      "driver-view": [],
      "driver-view-with-answer": [],
    },
    "correct-feedback": [""],
    "correct-feedback-practice": [""],
    "incorrect-feedback": [""],
    "incorrect-feedback-practice": [""],
  },
  instructionsAM: {
    instructions: {
      "driver-view": [],
      question: [],
    },
    "instructions-practice": {
      "driver-view": [],
      question: [],
      "question-with-answer": [],
    },
    "correct-rvm": [],
    "correct-grid": [],
    "incorrect-feedback": {
      "incorrect-glance": {
        "err-duration": [],
        "err-hazard": [],
        "err-duration-hazard": [],
        "err-no-glance": [],
      },
      "incorrect-grid": [],
    },
    "incorrect-feedback-practice": {
      "incorrect-glance": {
        "err-duration": [],
        "err-hazard": [],
        "err-duration-hazard": [],
        "err-no-glance": [],
      },
      "incorrect-grid": [],
    },
  },
  responseHistory: {
    correct: 0,
    incorrect: 0,
    last: null,
  },
  activityState: {
    taskId: null,
    moduleId: null,
    scenarioId: null,
    activityId: null,
    assetPath: null,
    practiceMode: null,
    type: null,
  },
  dashboardState: {
    email: null,
    emailHash: null,
    firstName: null,
    lastName: null,
    participantId: null,
    role: null,
    postDrivingTestActivated: null,
    inPerson: null,
  },
  feedback: { type: "", content: "" },
};

/** @type {InputFilter[]} */
export const emailFilters = [
  (v) => v.replace(/\s/g, ""),
  (v) => v.replace(/[@]+/g, "@"),
];

const emptyBanner = { icon: null, text: "" };
export const BannerContext = createContext([emptyBanner, null]);

/**
 * @param {Set} set
 * @param {string} value
 */
export function toggleSetValue(set, value) {
  if (set?.has?.(value)) {
    set?.delete?.(value);
  } else {
    set?.add?.(value);
  }
  return set;
}

// Function to encrypt the password with the public key
export function encryptWithPublicKey(password) {
  // Convert the public key to CryptoKey object
  const publicKeyObj = forge.pki.publicKeyFromPem(publicKey);
  // Encrypt the password using the public key
  const encrypted = publicKeyObj.encrypt(password, "RSA-OAEP", {
    md: forge.md.sha256.create(),
  });

  // Return the encrypted password as Base64 string
  return forge.util.encode64(encrypted);
}

export function mask(str = "") {
  // mask at most 4 characters, but at least 1/3 of the string
  const section = Math.ceil(Math.min(str.length / 3, 4));
  const hidden = str.slice(0, -section);
  const shown = str.slice(-section);
  return hidden.replace(/./g, "\u2022") + shown;
}

export async function pause(ms = 200) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Make loaders less janky with consistent delay.
 *
 * - If given action returns before delay time - remaining time is awaited
 * - Else - return result immediately
 *
 * @template T
 * @param {number} timeMS - Delay
 * @param {() => Promise<T>} action
 * @returns {Promise<T>}
 */
export async function loadFor(timeMS = 0, action) {
  const startTime = Date.now();

  const res = await action?.();

  const timeLeft = timeMS + startTime - Date.now();
  if (timeLeft > 0) await pause(timeLeft);
  return res;
}

export function duplicate(element = <></>, count = 1) {
  return (
    <>
      {Array.from({ length: count }, (_v, key) => {
        return cloneElement(element, { key: key, idx: key + 1 });
      })}
    </>
  );
}

/**
 * @template T
 * @param {object} obj
 * @param {T | keyof T} filter
 * @returns {T | Map<keyof T, any>}
 */
export function getSubset(obj = {}, filter = {}) {
  let filterKeys = Object.keys(filter);
  if (filter instanceof Array) {
    filterKeys = filter;
  }
  return filterKeys.reduce((subset, key) => {
    subset[key] = obj[key];
    return subset;
  }, {});
}

/**
 * @template Obj
 * @param {Obj} obj
 * @param {(keyof Obj)[]} filter
 * @returns {Map<keyof Obj, any>}
 */
export function getSubsetAlt(obj = {}, filter = []) {
  return filter.reduce((subset, key) => {
    subset[key] = obj[key];
    return subset;
  }, {});
}

export function keyEnter(event, func) {
  if (event && event.key === "Enter") {
    return func?.(event);
  }
}

/**
 * @param {Element} element
 * @param {string} varName
 */
export function getCssVar(element, varName) {
  if (!element) return;
  return window.getComputedStyle(element).getPropertyValue(varName);
}

export function xlsxToJson(file) {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: "array" });
      const firstSheet = workbook.SheetNames[0];
      const jsonData = utils.sheet_to_json(workbook.Sheets[firstSheet]);
      resolve(jsonData);
    };
    reader.onerror = (e) => {
      reject(e);
    };
    reader.readAsArrayBuffer(file);
  });
}

export function areCookiesEnabled() {
  let cookieEnabled = navigator.cookieEnabled;
  if (!cookieEnabled) {
    document.cookie = "testcookie";
    cookieEnabled = document.cookie.indexOf("testcookie") !== -1;
    if (cookieEnabled) {
      document.cookie = "testcookie=; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
    return cookieEnabled;
  }
  return true;
}

export function isModulePauseEnd(startTime, pauseMins = 60) {
  const totalTime = 1000 * 60 * pauseMins;
  return parseInt(startTime) + totalTime - Date.now() <= 0;
}

export function isFirstActivityInTrainingModule({
  taskId = "",
  scenarioId = "",
  activityId = "",
}) {
  const scenarioNum = parseInt(scenarioId.split("_")[1]);
  return (
    taskId === "training" &&
    scenarioNum === 1 &&
    (activityId === "S" || activityId === "AM")
  );
}

export function isLastActivityInTrainingModule({
  taskId = "",
  scenarioId = "",
  activityId = "",
}) {
  const scenarioNum = parseInt(scenarioId.split("_")[1]);
  return (
    taskId === "training" &&
    scenarioNum === 6 &&
    (activityId === "T" || activityId === "AM")
  );
}

export function getCurrentActivityIndex({ moduleId, scenarioId, activityId }) {
  return `${moduleId}_${scenarioId}_${activityId}`;
}

export function prevActivity(curActivity, activityOrder) {
  const currentActivityIndex = getCurrentActivityIndex(curActivity);
  const prevActivityIndex = activityOrder[currentActivityIndex]?.prev;

  if (activityOrder[prevActivityIndex]?.practiceMode) return null;

  return prevActivityIndex && activityOrder[prevActivityIndex];
}

export function nextActivity(curActivity, activityOrder) {
  const currentActivityIndex = getCurrentActivityIndex(curActivity);
  const nextActivityIndex = activityOrder[currentActivityIndex]?.next;

  return nextActivityIndex && activityOrder[nextActivityIndex];
}

const notPractice = (a) => !a?.practiceMode;

export function calcNumActivities(activityOrder) {
  return Object.values(activityOrder).filter(notPractice).length;
}

export function calcModuleProgress(activity, activityOrder) {
  return calcTaskProgress(activity, activityOrder, true);
}

export function calcTaskProgress(activity, activityOrder, sameModule = false) {
  if (activity.practiceMode) return 0;
  if (activity.isCompleted) return 100;

  const activityIdx = getCurrentActivityIndex(activity);

  if (activityOrder?.[activityIdx]?.practiceMode) return 0;

  const countedActivities = Object.entries(activityOrder)
    .filter(([_, activity]) => notPractice(activity))
    .filter(([_, a]) => (sameModule ? a.moduleId === activity.moduleId : true))
    .map(([idx, _]) => idx);

  const numComplete = countedActivities.indexOf(activityIdx);

  if (numComplete < 0) return 0;

  return Math.floor((numComplete / countedActivities.length) * 100);
}

export function calcTotalProgress(activity, activityOrders) {
  let afterCurTask = false,
    numComplete = 0,
    numTotal = 0;

  let curTask = activity.taskId;
  const tasks = Object.keys(activityOrders);
  const taskIdx = tasks.indexOf(curTask);
  if (activity.isCompleted) curTask = tasks[taskIdx + 1];

  if (!curTask) return 100;

  Object.entries(activityOrders).forEach(([taskId, order]) => {
    if (afterCurTask) {
      numTotal += calcNumActivities(order);
    } else if (curTask === taskId) {
      afterCurTask = true;

      const activityIdx = getCurrentActivityIndex(activity);
      const countedActivities = Object.entries(order)
        .filter(([_, activity]) => notPractice(activity))
        .map(([idx, _]) => idx);

      numTotal += countedActivities.length;
      numComplete += countedActivities.indexOf(activityIdx);
    } else {
      const previouslyCompleted = calcNumActivities(order);
      numTotal += previouslyCompleted;
      numComplete += previouslyCompleted;
    }
  });

  if (numComplete < 0) return 0;

  return Math.floor((numComplete / numTotal) * 100);
}

export function getTaskName(taskId = "", role = "") {
  if (!taskId) return null;
  const taskNames = {
    training: "Attention Maintenance Training",
    postDrivingTest: "Attention Maintenance Quiz",
  };
  return taskNames[taskId];
}

export function getSkillType(moduleId = "") {
  if (!moduleId) return null;
  const skills = ["Hazard Anticipation", "Attention Maintenance"];
  const moduleNum = parseInt(moduleId.split("_")[1]);
  const skillIdx = moduleNum % 2 || 2;
  return skills[skillIdx - 1];
}

export function getModuleName(moduleId = "") {
  if (!moduleId) return null;
  const moduleNum = moduleId.split("_")[1];
  return `Module ${moduleNum}`;
}

export function getScenarioType(moduleId = "") {
  if (!moduleId) return null;
  const scenarioTypes = ["Intersections", "Rear-Ends", "Curves"];
  const moduleNum = parseInt(moduleId.split("_")[1]) - 1;
  const typeIdx = Math.floor(moduleNum / 2);
  return scenarioTypes[Math.min(typeIdx, 2)];
}

export function getScenarioName(scenarioId = "") {
  if (!scenarioId) return null;
  const scenario = scenarioId.split("_");
  let scenarioType = "";
  if (scenario[0] === "intersections") {
    scenarioType = "Intersections";
  } else if (scenario[0] === "rearends") {
    scenarioType = "Rear-Ends";
  } else {
    scenarioType = "Curves";
  }
  // return `${scenarioType} ${scenario[1]}`;
  return `${scenarioType}`;
}

// TODO: Change Tactical Activity/Strategic Activity to Near View/Far View
export function getActivityName(activityId = "") {
  if (!activityId) return null;
  return activityId === "S" ? "Far View" : "Near View";
}

export function getModuleActivityNumber(scenarioId = "", activityId = "") {
  const scenarioNum = scenarioId.split("_")[1];
  return 2 * scenarioNum - (activityId === "S");
}

export function abbrevScenario(scenarioId) {
  const scenarioName = getScenarioName(scenarioId);
  return scenarioName[0] + scenarioName.slice(-1);
}

export function abbrevSkill(moduleId) {
  const skill = getSkillType(moduleId).split(" ");
  return skill[0][0] + skill[1][0];
}

export function getEntrance(entry, activityState) {
  const { moduleId, activityId } = activityState || {};

  if (moduleId && activityId) {
    const scenarioEntry = [getScenarioName(moduleId)];
    if (activityId) {
      scenarioEntry.push(getActivityName(activityId));
    }
    return scenarioEntry.join(" - ");
  } else {
    return entry;
  }
}

export async function fetchTrainingProgress(pid, tid, mid) {
  if (mid) {
    return await axios
      .get(`${ServerUrl}/users/${pid}/tasks/${tid}/modules/${mid}/status`, {
        withCredentials: true,
      })
      .then((res) => res.data.data)
      .catch(console.log);
  } else {
    return await axios
      .get(`${ServerUrl}/users/${pid}/tasks/${tid}/status`, {
        withCredentials: true,
      })
      .then((res) => res.data.data)
      .catch(console.log);
  }
}

export async function fetchTestingScore(pid, tid, mid) {
  return await axios
    .get(`${ServerUrl}/users/${pid}/tasks/${tid}/modules/${mid}/status`, {
      withCredentials: true,
    })
    .then((res) => {
      const activityStateArray = res.data.data;
      return activityStateArray.filter((activity) => activity === "correct")
        .length;
    })
    .catch(console.log);
}
